import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { constant } from '@medflex/commons';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import Avatar from '@medflex/components/stories/Avatar';
import useClassNames from '@medflex/hooks/useClassNames';
import PropTypes from 'prop-types';
import Link from '@medflex/components/stories/Link';
import IconInformationOutline from '@medflex/components/stories/icons/InformationOutline';
import useUserProfile from '../../hooks/useUserProfile';
import useCountries from '../../hooks/useCountries';
import useModal from '../../hooks/useModal';
import { MODALS } from '../../hooks/useIsModal';

const ConfigItem = ({ label, value, className, ...props }) => {
  const cn = useClassNames(() => ['mb-2', className], [className]);
  return (
    <li data-component="PhysicianConfigModule__ConfigItem" className={cn} {...props}>
      <div className="text-slate-500">{label}</div>
      <div>{value}</div>
    </li>
  );
};

ConfigItem.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};

ConfigItem.defaultProps = {
  value: '',
};

const PhysicianConfigModule = () => {
  const { userProfile, isLoadingUserProfile } = useUserProfile();
  const { t } = useTranslation('physician', { keyPrefix: 'PhysicianConfigModule' });
  const { t: tGlobal } = useTranslation('global');
  const { open } = useModal();

  const countries = useCountries();

  const {
    email,
    gender,
    firstName,
    lastName,
    title,
    areaOfActivity = 'PRACTICE',
    workArea1: workArea,
    address,
    address2,
    postalCode,
    city,
    country,
    avatar,
    initials,
    fullNameWithTitle,
    organization,
    organizationalForm,
    usedFeatures,
    mobileNumber,
    phoneNumber,
    workplace,
    notificationSettings,
  } = useMemo(() => {
    if (userProfile) {
      const { attributes } = userProfile;
      return {
        ...attributes,
        gender: attributes.gender ? tGlobal(`gender.${attributes.gender}`) : null,
        country: attributes.countryCode ? countries[attributes.countryCode] : null,
        areaOfActivity: attributes.areaOfActivity
          ? constant.areaOfActivity[attributes.areaOfActivity]
          : null,
        usedFeatures:
          attributes.usedFeatures &&
          attributes.usedFeatures.map(feature => constant.usedFeatures[feature]).join(', '),
        organizationalForm:
          attributes.organizationalForm &&
          constant.organizationalForm[attributes.organizationalForm],
      };
    }
    return null;
  }, [countries, t, userProfile]);

  return (
    !isLoadingUserProfile && (
      <div data-component="PhysicianConfigModule" className="flex flex-col space-y-6">
        <header className="flex items-center">
          <h2 className="text-2xl font-semibold">{t('title')}</h2>
        </header>
        <p>{t('description')}</p>
        <SettingsBox
          title={t('profile_image.title')}
          canEdit
          onEdit={() => open({ modal: MODALS.editProfileImage })}
        >
          <div className="flex flex-row items-center">
            <Avatar
              className="flex-shrink-0"
              alt={fullNameWithTitle}
              src={avatar}
              initials={initials}
              size="12"
            />
          </div>
        </SettingsBox>
        <SettingsBox
          title={t('profile_personal.title')}
          canEdit
          onEdit={() => open({ modal: MODALS.profile_personal })}
        >
          <ul>
            <ConfigItem
              label={t('profile_personal.input.salutation.label')}
              value={gender || t('profile_personal.input.salutation.placeholder')}
            />
            <ConfigItem
              label={t('profile_personal.input.title.label')}
              value={title || t('profile_personal.input.title.placeholder')}
            />
            <ConfigItem
              label={t('profile_personal.input.firstName.label')}
              value={firstName || t('profile_personal.input.firstName.placeholder')}
            />
            <ConfigItem
              label={t('profile_personal.input.lastName.label')}
              value={lastName || t('profile_personal.input.lastName.placeholder')}
            />
            <ConfigItem
              label={t('profile_personal.input.email.label')}
              value={email || t('profile_personal.input.email.placeholder')}
            />
            <ConfigItem
              label={t('profile_personal.input.mobileNumber.label')}
              value={mobileNumber || t('profile_personal.input.mobileNumber.placeholder')}
            />
            <ConfigItem
              label={t('profile_personal.input.phoneNumber.label')}
              value={phoneNumber || t('profile_personal.input.phoneNumber.placeholder')}
            />
          </ul>
        </SettingsBox>
        <SettingsBox
          title={t('profile_clinic.title')}
          canEdit
          onEdit={() => open({ modal: MODALS.profile_clinic })}
        >
          <ul>
            <ConfigItem
              label={t('profile_clinic.input.name.label')}
              value={workplace || t('profile_clinic.input.name.placeholder')}
            />
            <ConfigItem
              label={
                <div className="flex items-center space-x-2">
                  <div>{t('profile_clinic.input.organization.label')}</div>
                  <Link
                    target="_blank"
                    href="https://medflex.de/hilfe/behandler-starten/organisationen-abbilden/"
                  >
                    <IconInformationOutline size="4" />
                  </Link>
                </div>
              }
              value={
                (organization && organization.name) ||
                t('profile_clinic.input.organization.placeholder')
              }
            />
            <ConfigItem
              label={t('profile_clinic.input.areaOfActivity.label')}
              value={areaOfActivity || t('profile_clinic.input.areaOfActivity.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.workArea.label')}
              value={workArea || t('profile_clinic.input.workArea.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.organizationalForm.label')}
              value={organizationalForm || t('profile_clinic.input.organizationalForm.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.usedFeatures.label')}
              value={usedFeatures || t('profile_clinic.input.usedFeatures.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.address.label')}
              value={address || t('profile_clinic.input.address.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.address2.label')}
              value={address2 || t('profile_clinic.input.address2.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.postalCode.label')}
              value={postalCode || t('profile_clinic.input.postalCode.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.city.label')}
              value={city || t('profile_clinic.input.city.placeholder')}
            />
            <ConfigItem
              label={t('profile_clinic.input.country.label')}
              value={country || t('profile_clinic.input.country.placeholder')}
            />
          </ul>
        </SettingsBox>
        <SettingsBox
          title={t('profile_sms.title')}
          canEdit
          onEdit={() => open({ modal: MODALS.smsSettingsEdit })}
        >
          <p className="text-slate-600 mb-2">{t('profile_sms.description')}</p>
          {notificationSettings.sms?.senderName && (
            <ul>
              <ConfigItem
                label={t('profile_sms.label')}
                value={notificationSettings.sms.senderName}
              />
            </ul>
          )}
        </SettingsBox>
      </div>
    )
  );
};

export default PhysicianConfigModule;
